import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const emptyDraft = () => ({
  name: '',
  kpiKey: '',
  moduleKey: 'dashboard',
  pageKey: 'overview',
  dataSourceKey: 'qaqc_items',
  aggregation: 'count',
  measureField: '',
  statusValue: '',
  daysAhead: '30',
  drillDownTabKey: 'qaqc',
  displayOrder: 100,
  status: 'ACTIVE',
  projectScope: 'CURRENT_USER',
});

const ConfigKpiBuilder = ({ pageKey }) => {
  const [rows, setRows] = useState([]);
  const [sources, setSources] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [filterPage, setFilterPage] = useState(pageKey || '');
  const [classFilter, setClassFilter] = useState('all');
  const [draft, setDraft] = useState(emptyDraft());
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    const res = await adminAPI.getConfigKpis();
    setRows(res?.data?.data || []);
    setEnabled(res?.data?.enabled === true);
    setSources(res?.data?.dataSources || []);
  }, []);

  useEffect(() => {
    reload().catch(() => notifyError('Unable to load KPIs'));
    adminAPI.getConfigFlags().then((r) => {
      const f = r?.data?.flags || {};
      if (f.ENABLE_KPI_BUILDER === true) setEnabled(true);
    }).catch(() => {});
  }, [reload]);

  useEffect(() => { if (pageKey) setFilterPage(pageKey); }, [pageKey]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filterPage) list = list.filter((k) => k.page?.pageKey === filterPage);
    if (classFilter !== 'all') list = list.filter((k) => k.kpiClass === classFilter);
    return list;
  }, [rows, filterPage, classFilter]);

  const pages = useMemo(() => {
    const map = new Map();
    rows.forEach((k) => {
      if (k.page?.pageKey) map.set(k.page.pageKey, k.page.displayName);
    });
    return Array.from(map.entries());
  }, [rows]);

  const selectedSource = sources.find((s) => s.key === draft.dataSourceKey);

  const buildDefinition = () => {
    const def = {};
    if (draft.statusValue && draft.aggregation === 'status_count') {
      const field = draft.measureField || (selectedSource?.filterFields || [])[0];
      if (field) def.equals = { [field]: draft.statusValue };
      def.statusValue = draft.statusValue;
    }
    if (draft.aggregation === 'expiring_soon' && draft.daysAhead) {
      def.daysAhead = Number(draft.daysAhead) || 30;
    }
    return Object.keys(def).length ? def : null;
  };

  const createKpi = async () => {
    if (!draft.name.trim()) {
      notifyError('Name is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.createConfigKpi({
        ...draft,
        measureField: draft.measureField || null,
        definitionJson: buildDefinition(),
      });
      notifySuccess('Custom KPI created');
      setDraft(emptyDraft());
      setPreview(null);
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const previewDraft = async () => {
    try {
      const res = await adminAPI.previewConfigKpiDraft({
        dataSourceKey: draft.dataSourceKey,
        aggregation: draft.aggregation,
        measureField: draft.measureField || null,
        definitionJson: buildDefinition(),
        projectScope: draft.projectScope,
      });
      setPreview(res?.data?.data || res?.data);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Preview failed');
    }
  };

  const previewExisting = async (k) => {
    try {
      const res = await adminAPI.previewConfigKpi(k.id);
      setPreview({ ...res?.data?.data, kpiKey: k.kpiKey, name: k.name });
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Preview failed');
    }
  };

  const archiveKpi = async (k) => {
    if (k.isSystemKpi || k.kpiClass === 'SYSTEM') {
      if (!(await confirmDialog('Archive this System KPI (hides from custom runtime; inventory kept)?', 'Archive'))) return;
      try {
        await adminAPI.updateConfigKpi(k.id, { status: 'ARCHIVED' });
        notifySuccess('System KPI archived');
        await reload();
      } catch (err) {
        notifyError(err?.response?.data?.message || 'Archive failed');
      }
      return;
    }
    if (!(await confirmDialog(`Archive custom KPI "${k.name}"?`, 'Archive KPI'))) return;
    try {
      await adminAPI.deleteConfigKpi(k.id);
      notifySuccess('KPI archived');
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Archive failed');
    }
  };

  return (
    <div>
      <p className="ce-muted">
        Phase 6: System KPIs are presentation-protected. Custom KPIs use the approved data-source registry only (no raw SQL).
        {enabled ? '' : ' ENABLE_KPI_BUILDER is off.'}
      </p>

      {enabled && (
        <div className="ce-detail" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Create custom KPI</h3>
          <div className="form-row">
            <div className="form-group"><label>Name *</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div className="form-group"><label>Key</label><input value={draft.kpiKey} placeholder="auto" onChange={(e) => setDraft({ ...draft, kpiKey: e.target.value })} /></div>
            <div className="form-group"><label>Module</label>
              <select value={draft.moduleKey} onChange={(e) => setDraft({ ...draft, moduleKey: e.target.value })}>
                <option value="dashboard">dashboard</option>
                <option value="technical">technical</option>
                <option value="financials">financials</option>
                <option value="admin_panel">admin_panel</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Data source *</label>
              <select value={draft.dataSourceKey} onChange={(e) => setDraft({ ...draft, dataSourceKey: e.target.value, aggregation: 'count', measureField: '' })}>
                {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Aggregation *</label>
              <select value={draft.aggregation} onChange={(e) => setDraft({ ...draft, aggregation: e.target.value })}>
                {(selectedSource?.allowedAggregations || ['count']).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Measure field</label>
              <select value={draft.measureField} onChange={(e) => setDraft({ ...draft, measureField: e.target.value })}>
                <option value="">—</option>
                {(selectedSource?.measureFields || []).map((f) => <option key={f} value={f}>{f}</option>)}
                {(selectedSource?.filterFields || []).map((f) => <option key={`f-${f}`} value={f}>{f} (status/result)</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            {(draft.aggregation === 'status_count') && (
              <div className="form-group"><label>Status / result value</label><input value={draft.statusValue} placeholder="e.g. Fail" onChange={(e) => setDraft({ ...draft, statusValue: e.target.value })} /></div>
            )}
            {draft.aggregation === 'expiring_soon' && (
              <div className="form-group"><label>Days ahead</label><input type="number" value={draft.daysAhead} onChange={(e) => setDraft({ ...draft, daysAhead: e.target.value })} /></div>
            )}
            <div className="form-group"><label>Drill-down tab</label>
              <select value={draft.drillDownTabKey} onChange={(e) => setDraft({ ...draft, drillDownTabKey: e.target.value })}>
                <option value="">—</option>
                {(selectedSource?.drillDownTabs || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Project scope</label>
              <select value={draft.projectScope} onChange={(e) => setDraft({ ...draft, projectScope: e.target.value })}>
                <option value="CURRENT_USER">CURRENT_USER</option>
                <option value="ALL">ALL</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn-secondary btn-sm" onClick={previewDraft}>Preview value</button>
            <button type="button" className="btn-primary btn-sm" disabled={saving} onClick={createKpi}>{saving ? 'Saving…' : 'Create KPI'}</button>
            {preview && (
              <span className="ce-muted" style={{ alignSelf: 'center' }}>
                Preview{preview.name || preview.kpiKey ? ` (${preview.name || preview.kpiKey})` : ''}: <strong>{preview.value ?? '—'}</strong>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="filter-bar">
        <select value={filterPage} onChange={(e) => setFilterPage(e.target.value)}>
          <option value="">All pages</option>
          {pages.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
        </select>
        <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
          <option value="all">All classes</option>
          <option value="SYSTEM">SYSTEM</option>
          <option value="CUSTOM">CUSTOM</option>
        </select>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th><th>Key</th><th>Class</th><th>Module</th><th>Page</th>
              <th>Aggregation</th><th>Data source</th><th>Drill-down</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>No KPIs</td></tr>
            ) : filtered.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td><code>{k.kpiKey}</code></td>
                <td><span className={`ce-badge ${k.kpiClass === 'SYSTEM' ? 'ce-badge--warn' : 'ce-badge--ok'}`}>{k.kpiClass}</span></td>
                <td>{k.moduleKey || '—'}</td>
                <td>{k.page?.displayName || '—'}</td>
                <td>{k.aggregation || '—'}</td>
                <td>{k.dataSourceKey || '—'}</td>
                <td>{k.drillDownTabKey || '—'}</td>
                <td>{k.status}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {enabled && <button type="button" className="btn-secondary btn-sm" onClick={() => previewExisting(k)}>Preview</button>}
                  {enabled && <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: 6, color: '#b91c1c' }} onClick={() => archiveKpi(k)}>Archive</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConfigKpiBuilder;
